import { useEffect, useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Switch } from '@verlo/shared/components/ui/switch';
import { OptionChips } from '@verlo/shared/components/ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Link } from 'react-router-dom';
import { recipesApi, financialConfigApi } from '@verlo/shared/services/api';
import { toast } from 'sonner';
import { Loader2, Plus, X } from 'lucide-react';
import ModifierGroupsEditor, { normaliseGroups } from './ModifierGroupsEditor';
import { fromTillPrices, typeItemPrice, withTillPrices } from '../../lib/choicePrices';
import MealGroupsEditor, { normaliseMealGroups } from './MealGroupsEditor';
import HoursEditor, { normaliseHours } from './HoursEditor';
import { ALLERGENS as ALLERGEN_LIST, canonical } from '@verlo/shared/lib/allergens';
import { isBarSection, routeFor, routeWords } from '@verlo/shared/lib/kitchenRoutes';
import { useSite } from '../../contexts/SiteContext';
import RoutePicker from './RoutePicker';

// The one item dialog (menu consolidation, wave 1). Every field the three old dialogs had
// between them, in three groups: what the till shows, what it costs, what's in it.

export const EMPTY_ITEM = {
  name: '', description: '', price: '', cost: '', category: '', image_url: '',
  is_available: true, promoted: false,
  is_vegetarian: false, is_vegan: false, is_gluten_free: false,
  allergens: [], modifiers: [], size_options: [], modifier_groups: [], meal_groups: [], availability: [], revenue_line: '', always_ask: false, plu: '', kitchen_station: '', kitchen_route: [], kitchen_own: false,
};

// The UK's 14, shared with the till so a dish and a table's allergy are in the same words.
// The old twelve-item list wrote `dairy`, `nuts` and `shellfish` and had no lupin or
// molluscs; an item saved with those words opens here as the allergens they mean.
/** An item's allergens as the 14, keeping any word outside them so a save never drops it. */
function openAllergens(words) {
  const extra = (words || []).filter((w) => canonical([w]).length === 0);
  return [...canonical(words), ...extra];
}

export const ALLERGENS = ALLERGEN_LIST.map((a) => a.key);

const NEW_SECTION = '__new__';

const Group = ({ title, children }) => (
  <section className="space-y-3">
    <h3 className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, hint, children, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-[12.5px] text-ink-soft">{label}</Label>
    {children}
    {hint && <p className="text-[12px] text-ink-faint">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-3 rounded-md border border-hairline px-3 py-2 text-[13.5px] text-ink">
    {label}
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const gbp = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);

// The item's recipe, read-only: the Recipes page edits it, this shows what it costs.
const RecipePanel = ({ itemId, onCost }) => {
  const [state, setState] = useState({ loading: true, recipe: null });
  useEffect(() => {
    let active = true;
    setState({ loading: true, recipe: null });
    recipesApi.getByMenuItem(itemId)
      .then((res) => { if (active) setState({ loading: false, recipe: res.data?.recipe || null }); })
      .catch(() => { if (active) setState({ loading: false, recipe: null }); });
    return () => { active = false; };
  }, [itemId]);
  const lines = state.recipe?.ingredients || [];
  const total = state.recipe?.total_cost ?? lines.reduce((s, l) => s + (l.cost || 0), 0);
  if (state.loading) return <p className="text-[12.5px] text-ink-faint">Looking for a recipe…</p>;
  if (!state.recipe || lines.length === 0) {
    return (
      <p className="text-[12.5px] text-ink-soft" data-testid="menu-ingredient-empty">
        No recipe yet. <Link to="/back-office/recipes" className="text-verlo underline-offset-2 hover:underline">Build one on Recipes</Link> and the cost is worked out from it.
      </p>
    );
  }
  return (
    <div className="rounded-md border border-hairline">
      {lines.map((l, i) => (
        <div key={i} className="flex items-center justify-between border-b border-hairline px-3 py-1.5 text-[12.5px] last:border-0" data-testid="menu-ingredient-row">
          <span className="text-ink">{l.name} <span className="text-ink-faint">· {l.quantity}{l.unit ? ` ${l.unit}` : ''}</span></span>
          <span className="font-mono tabular-nums text-ink-soft">{gbp(l.cost)}</span>
        </div>
      ))}
      <div className="flex items-center justify-between bg-surface-2 px-3 py-1.5 text-[12.5px]">
        <span className="text-ink-soft">Recipe cost</span>
        <span className="flex items-center gap-2">
          <span className="font-mono tabular-nums text-ink">{gbp(total)}</span>
          {onCost && <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={() => onCost(total)}>Use as cost</Button>}
        </span>
      </div>
    </div>
  );
};

const MenuItemDialog = ({ item, sections, modifiers, menuItems = [], onClose, onSave }) => {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [newSection, setNewSection] = useState('');
  const [custom, setCustom] = useState(false);   // "New section…" chosen
  const [saving, setSaving] = useState(false);
  const open = item !== null && item !== undefined;
  // The kitchen stations and section routes of the site this dish is on, for "In the
  // kitchen". Without stations, the field isn't shown.
  const { site } = useSite();
  const kitchenSite = item?.site_id || site || undefined;
  const [kitchen, setKitchen] = useState({ stations: [], routes: {} });
  useEffect(() => {
    if (!open) return;
    financialConfigApi.getSettings(kitchenSite)
      .then(({ data }) => setKitchen({ stations: data.kitchen_stations || [], routes: data.kitchen_routes || {} }))
      .catch(() => setKitchen({ stations: [], routes: {} }));
  }, [open, kitchenSite]);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY_ITEM,
      ...item,
      price: item.price == null || item.price === '' ? '' : Number(item.price).toFixed(2),
      cost: item.cost || '',
      allergens: openAllergens(item.allergens),
      modifiers: item.modifiers || [],
      // A size is shown as its own price (Martin, 27 Oct). The seed stores sizes that way; an
      // older one stored as an amount added is read as the price it comes to.
      size_options: (item.size_options || item.sizes || []).map((s) => ({
        name: s.name || '',
        price: s.price != null ? Number(s.price).toFixed(2) : (Number(item.price || 0) + Number(s.price_modifier || 0)).toFixed(2),
      })),
      modifier_groups: withTillPrices(item.modifier_groups || [], Number(item.price || 0)),
      meal_groups: item.meal_groups || [],
      availability: item.availability || [],
      revenue_line: item.revenue_line || '',
      kitchen_station: item.kitchen_station || '',
      // A dish's old single station reads as a one-stop route of its own.
      kitchen_route: item.kitchen_route?.length ? item.kitchen_route : (item.kitchen_station ? [item.kitchen_station] : []),
      kitchen_own: !!(item.kitchen_route?.length || item.kitchen_station),
      always_ask: !!item.always_ask,
      plu: item.plu ?? '',
    });
    const unknown = !!item.category && !sections.some((s) => s.name === item.category);
    setCustom(unknown);
    setNewSection(unknown ? item.category : '');
  }, [item, open, sections]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // The item's price: a choice priced the same as the item moves with it (Medium, at no extra);
  // one with its own till price holds still. A new default sets the price without moving any.
  // `till`: typed in a default option's own cell ([group, option]), which is set in the same
  // change as the follow (lib/choicePrices typeItemPrice).
  const setPrice = (v, { follow = true, till = null } = {}) => setForm((f) => ({
    ...f, price: v, modifier_groups: typeItemPrice(f.modifier_groups, f.price, v, { follow, till }),
  }));
  const sectionValue = custom ? NEW_SECTION : form.category || '';

  const submit = async () => {
    const category = (custom ? newSection : form.category).trim();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.price === '' || isNaN(parseFloat(form.price))) { toast.error('Price is required'); return; }
    if (!category) { toast.error('Pick a section'); return; }
    if (form.kitchen_own && !form.kitchen_route.length) { toast.error('Pick the stations it goes through, or let it follow its section'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form, category,
        modifier_groups: normaliseGroups(fromTillPrices(form.modifier_groups, parseFloat(form.price) || 0)),
        meal_groups: normaliseMealGroups(form.meal_groups),
        availability: normaliseHours(form.availability),
        revenue_line: form.revenue_line || null,
        // A route replaces the old single station, which is cleared as it's saved.
        kitchen_route: form.kitchen_own ? form.kitchen_route : [],
        kitchen_station: null,
        plu: form.plu === '' ? null : Number(form.plu),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the item");
    } finally {
      setSaving(false);
    }
  };

  const sizes = form.size_options || [];
  const setSize = (i, k, v) => set('size_options', sizes.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sheet" data-testid="menu-item-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? form.name || 'Item' : 'Add item'}</DialogTitle>
          <DialogDescription>
            {form.id ? 'Changes reach every till at the next sync.' : 'A name, a price and a section is enough to start.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-6">
          <Group title="On the till">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_120px_96px]">
              <Field label="Name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="item-name" autoFocus /></Field>
              <Field label="Price"><Input type="number" step="0.01" min="0" inputMode="decimal" className="font-mono tabular-nums" value={form.price} onChange={(e) => setPrice(e.target.value)} data-testid="item-price" /></Field>
              <Field label="PLU" hint="blank = next free"><Input type="number" min="1" inputMode="numeric" className="font-mono tabular-nums" value={form.plu} onChange={(e) => set('plu', e.target.value)} placeholder="auto" data-testid="item-plu" /></Field>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Section">
                <Select value={sectionValue} onValueChange={(v) => { if (v === NEW_SECTION) { setCustom(true); setNewSection(''); } else { setCustom(false); set('category', v); } }}>
                  <SelectTrigger data-testid="item-section"><SelectValue placeholder="Pick a section" /></SelectTrigger>
                  <SelectContent>
                    {sections.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                    <SelectItem value={NEW_SECTION}>New section…</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {custom && (
                <Field label="New section name">
                  <Input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="e.g. Small plates" data-testid="item-new-section" />
                </Field>
              )}
            </div>
            <Field label="Description" hint="Shows on the QR menu and the item card."><Textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} /></Field>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Toggle label="On the till" checked={form.is_available} onChange={(v) => set('is_available', v)} testId="item-available" />
              <Toggle label="Promoted (pinned to the top of its section)" checked={form.promoted} onChange={(v) => set('promoted', v)} testId="item-promoted" />
            </div>
            <Field label="Sizes" hint="Leave empty for one price. Each size's price is what the till charges for it.">
              <div className="space-y-2">
                {sizes.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={s.name} onChange={(e) => setSize(i, 'name', e.target.value)} placeholder="e.g. Pint" className="flex-1" />
                    <Input type="number" step="0.01" inputMode="decimal" value={s.price} onChange={(e) => setSize(i, 'price', e.target.value)} className="w-28 font-mono tabular-nums" placeholder="0.00" aria-label={`${s.name || 'Size'} till price`} data-testid={`item-size-${i}-price`} />
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => set('size_options', sizes.filter((_, j) => j !== i))} aria-label="Remove size"><X size={14} /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => set('size_options', [...sizes, { name: '', price: form.price || '' }])}><Plus size={14} className="mr-1" />Add a size</Button>
              </div>
            </Field>
          </Group>

          {/* Meals (30 Sep 2026). Above the modifier groups, because what a kids meal is
              made of is a bigger fact about the item than how it can be tweaked. */}
          <Group title="Made of other dishes">
            <Field label="Meal groups" hint="For a kids meal, a lunch combo or a set menu. Each choice is another item on the menu, and the amount is what the meal costs more, not what that dish sells for on its own.">
              <MealGroupsEditor
                value={form.meal_groups}
                onChange={(v) => set('meal_groups', v)}
                menuItems={menuItems}
                selfId={form.id || null}
                basePrice={parseFloat(form.price) || 0}
              />
            </Field>
          </Group>

          {kitchen.stations.length > 0 && !isBarSection(custom ? newSection : form.category) && (
            <Group title="In the kitchen">
              <div className="space-y-3 rounded-lg border border-hairline bg-white p-4" data-testid="item-kitchen">
                <label className="flex cursor-pointer items-start gap-2.5">
                  <input type="radio" name="kitchen-route" className="mt-1 accent-verlo" checked={!form.kitchen_own}
                         onChange={() => set('kitchen_own', false)} data-testid="item-kitchen-follow" />
                  <span>
                    <span className="block text-[14px] text-ink">Follow {(custom ? newSection : form.category) || 'its section'}</span>
                    <span className="block text-[12.5px] text-ink-soft" data-testid="item-kitchen-section-route">
                      {(() => {
                        const r = routeFor({ category: custom ? newSection : form.category }, kitchen.stations, kitchen.routes);
                        return r.length ? `${routeWords(r, kitchen.stations)}, then the Pass` : 'Straight to the Pass';
                      })()}
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2.5">
                  <input type="radio" name="kitchen-route" className="mt-1 accent-verlo" checked={form.kitchen_own}
                         onChange={() => set('kitchen_own', true)} data-testid="item-kitchen-own" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] text-ink">Its own route</span>
                    <span className="block text-[12.5px] text-ink-soft">Tap the stations in the order the food goes through them.</span>
                  </span>
                </label>
                {form.kitchen_own && (
                  <div className="pl-6">
                    <RoutePicker stations={kitchen.stations} value={form.kitchen_route.filter((k) => kitchen.stations.some((st) => st.key === k))}
                                 onChange={(r) => set('kitchen_route', r)} testId="item-kitchen-route" />
                  </div>
                )}
              </div>
            </Group>
          )}

          <Group title="When it's on the till">
            <Field label="Hours">
              <HoursEditor value={form.availability} onChange={(v) => set('availability', v)} what="this dish" />
            </Field>
          </Group>

          <Group title="Choices on the till">
            <Field label="Groups" hint="What the sheet asks when this item is long-pressed. A choose-one group is a row of cells, each at the price the till charges for it; extras are tick tiles, added on top.">
              <ModifierGroupsEditor value={form.modifier_groups} onChange={(v) => set('modifier_groups', v)} library={modifiers} basePrice={parseFloat(form.price) || 0}
                priceText={form.price} onBasePrice={setPrice} />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Toggle label="Always ask (the sheet opens on every tap)" checked={form.always_ask} onChange={(v) => set('always_ask', v)} testId="item-always-ask" />
              <Field label="Revenue line" hint="Where this item's money lands. Blank follows its section.">
                <Select value={form.revenue_line || 'auto'} onValueChange={(v) => set('revenue_line', v === 'auto' ? '' : v)}>
                  <SelectTrigger data-testid="item-revenue-line"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">From the section</SelectItem>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="drink">Drink</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Group>

          <Group title="Costing">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[120px_1fr]">
              <Field label="Cost"><Input type="number" step="0.01" min="0" inputMode="decimal" className="font-mono tabular-nums" value={form.cost} onChange={(e) => set('cost', e.target.value)} data-testid="item-cost" /></Field>
              <div className="flex items-end pb-2 text-[13px] text-ink-soft">
                {form.price > 0 && form.cost > 0
                  ? <span>Margin <span className="font-mono tabular-nums text-ink">{(((form.price - form.cost) / form.price) * 100).toFixed(0)}%</span></span>
                  : <span>Enter a cost for a margin, or take it from the recipe below.</span>}
              </div>
            </div>
            {form.id && <Field label="Recipe"><RecipePanel itemId={form.id} onCost={(c) => set('cost', c.toFixed(2))} /></Field>}
          </Group>

          <Group title="Dietary">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Toggle label="Vegetarian" checked={form.is_vegetarian} onChange={(v) => set('is_vegetarian', v)} />
              <Toggle label="Vegan" checked={form.is_vegan} onChange={(v) => set('is_vegan', v)} />
              <Toggle label="Gluten free" checked={form.is_gluten_free} onChange={(v) => set('is_gluten_free', v)} />
            </div>
            <Field label="Allergens">
              <OptionChips multiple value={form.allergens} onChange={(v) => set('allergens', v)} options={ALLERGEN_LIST.map((a) => ({ value: a.key, label: a.label }))} />
            </Field>
            <Field label="Image URL"><Input value={form.image_url || ''} onChange={(e) => set('image_url', e.target.value)} placeholder="https://…" /></Field>
          </Group>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={submit} disabled={saving} data-testid="item-save">
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
            {form.id ? 'Save' : 'Add item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MenuItemDialog;
